/**
 * Element registration (RFC §4): `defineElement(tag, ctor)` performs
 * `customElements.define` — and the `HTMLElementTagNameMap` augmentation lives
 * NEXT TO IT, in one place per app: the app's single `elements.ts` manifest.
 * No split declarations.
 *
 * The type augmentation itself is necessarily app-side (TypeScript `declare
 * global` merges at the declaration site). The manifest pattern:
 *
 * ```ts
 * // app/elements.ts — the ONE registration manifest
 * import { defineElement } from "@platform/web-kit";
 * import { OpportunityCard } from "./elements/opportunity-card";
 *
 * defineElement("career-opportunity-card", OpportunityCard);
 *
 * declare global {
 *   interface HTMLElementTagNameMap {
 *     "career-opportunity-card": OpportunityCard;
 *   }
 * }
 * ```
 *
 * Tag names are domain nouns, kebab-case, app-prefixed (AX-1) — the
 * `${string}-${string}` constraint enforces the mandatory hyphen at the type
 * level; the AX auditor (later slice) enforces the vocabulary.
 */

/** A custom-element tag name (must contain a hyphen). */
export type TagName = `${string}-${string}`;

/**
 * Register a custom element. Idempotent for the SAME constructor (safe under
 * hot-reload/test re-runs); throws when the tag is already taken by a
 * DIFFERENT constructor.
 */
export function defineElement<T extends HTMLElement>(
  tag: TagName,
  ctor: CustomElementConstructor & (new () => T)
): void {
  const existing = customElements.get(tag);
  if (existing === ctor) return;
  if (existing) {
    throw new Error(
      `[web-kit] <${tag}> is already defined with a different constructor.`
    );
  }
  customElements.define(tag, ctor);
}
